import React, {
  useEffect, useState, useMemo, useCallback, Fragment, ReactElement,
} from 'react';
import Chip from '../../../components/chip/chip.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FilteredCell from '../../../components/filtered-cell/filtered-cell.component';
import TableRowForm from '../../../components/table-row-form/table-row-form.component';
import ActionButton from '../../../components/action-button/action-button.component';
import IconBlockError from '../../../components/icon-block-error/icon-block-error.component';
import IconBlockSuccess from '../../../components/icon-block-success/icon-block-success.component';
import Loader from '../../../components/loader/loader.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldCheckbox from '../../../components/form-field-checkbox/form-field-checkbox.component';
import FormFieldDate from '../../../components/form-field-date/form-field-date.component';
import FormFieldAddress from '../../../components/form-field-address/form-field-address.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import FieldAddressBlock from '../../../components/field-address-block/field-address-block.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import TableCellShort from '../../../components/table-cell-short/table-cell-short.component';
import TableCellExtended from '../../../components/table-cell-extended/table-cell-extended.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import useShiftState from '../../../hooks/use-shift-state';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import { TABLE_NAME_ASSET_LIST } from '../../../constants/system/table-names';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const AssetRowForm: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isActive,
    isSaving,
    isSaveDisabled,
    isInlineSaving,
    isMultiplePortfolioGroupView,
    assetIndex,
    assetData,
    portfolioData,
    assetPhotoFile,
    inputRefs,
    fileDropDeps,
    currentUser,
    tableColumnSettings,
    assetStatusList,
    measureUnitList,
    pricingTypeList,
    portfolioList,
    assetTypeList,
    assetOwnershipStructureTypeList,
    contactCompanyList,
    tagList,
    onInlineSave,
    onInlineCancel,
    onAssetPhotoChange,
    onAssetPhotoDelete,
    onAddressSearchCompleted,
    onAddressFieldKeyDown,
    onAssetTypesChange,
    onAssetSellersChange,
    onTagsChange,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const isShiftPressed: boolean = useShiftState();

  const [selectedAssetSellers, setSelectedAssetSellers]: [Contact | null, Function] = useState(null);
  const [selectedAssetTypes, setSelectedAssetTypes]: [AssetType | null, Function] = useState(null);
  const [selectedTags, setSelectedTags]: [Tag | null, Function] = useState(null);

  const assetId: number = assetData.id;
  const portfolioId: number = portfolioData.id;
  const formKeyAsset: string = FormService.getIndexedFormFieldKey(assetId);
  const formKeyPortfolio: string = FormService.getIndexedFormFieldKey(portfolioId);
  const formPrefix: string = `assetListData[${formKeyPortfolio}].assets[${formKeyAsset}]`;

  const menuItemPortfolioList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    isGrouped: isMultiplePortfolioGroupView,
    groupKey: 'group',
    groupItemLabelKey: 'name',
    optionItemLabelKey: 'name',
    optionList: portfolioList,
  }), [portfolioList, isMultiplePortfolioGroupView]);

  const menuItemAssetStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetStatusList,
  }), [assetStatusList]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemPricingTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: pricingTypeList,
  }), [pricingTypeList]);

  const menuItemAssetOwnershipStructureTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetOwnershipStructureTypeList,
  }), [assetOwnershipStructureTypeList]);

  const menuItemContactCompanySellerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedAssetSellers,
  }), [contactCompanyList, selectedAssetSellers]);

  const menuItemAssetTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetTypeList,
    selectedItemList: selectedAssetTypes,
  }), [assetTypeList, selectedAssetTypes]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const onAssetPhotoFileChange: Function = useCallback((file: File): void => {
    if (onAssetPhotoChange) {
      onAssetPhotoChange(portfolioId, assetId, file);
    }
  }, [portfolioId, assetId]);

  const onAssetPhotoFileDelete: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onAssetPhotoDelete) {
      onAssetPhotoDelete(portfolioId, assetId);
    }

    event.stopPropagation();
  }, [portfolioId, assetId]);

  const sellersChange: Function = useCallback((contactValues: Contact[]): void => {
    if (onAssetSellersChange) {
      onAssetSellersChange(portfolioId, assetId, contactValues);
    }

    setSelectedAssetSellers(contactValues);
  }, [portfolioId, assetId]);

  const assetTypesChange: Function = useCallback((assetTypeValues: AssetType[]): void => {
    if (onAssetTypesChange) {
      onAssetTypesChange(portfolioId, assetId, assetTypeValues);
    }

    setSelectedAssetTypes(assetTypeValues);
  }, [portfolioId, assetId]);

  const tagsChange: Function = useCallback((tagValues: Tag[]): void => {
    if (onTagsChange) {
      onTagsChange(assetId, portfolioId, tagValues);
    }

    setSelectedTags(tagValues);
  }, [assetId, portfolioId]);

  const tableAssetListFields: TableField[] = useMemo((): TableField[] => [
    {
      id: 1,
      name: 'photo',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCellShort isTextCenter>
          <FormFieldUpload
            isImage
            isInline
            isDroppable
            isListImage
            name={`${formPrefix}.mainPhoto`}
            uploadFile={assetPhotoFile}
            buttonLabel={assetData.name}
            defaultPreviewUrl={assetData.mainPhotoThumbnail || assetData.mainPhoto || AssetDefaultImage}
            acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
            dropDeps={fileDropDeps}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
            onFileChange={onAssetPhotoFileChange}
            onFileDelete={onAssetPhotoFileDelete}
          />
        </TableCellShort>
      ),
    },
    {
      id: 2,
      name: 'name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isSticky isStickyLeft>
          <FormFieldText
            name={`${formPrefix}.name`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 3,
      name: 'portfolio.name',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = portfolioList && !portfolioList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.portfolioId`}
              optionsTotal={portfolioList && portfolioList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}` : null}
            >
              {menuItemPortfolioList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 4,
      name: 'lastDeal.name',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {assetData.lastDeal && assetData.lastDeal.name}
        </TableCell>
      ),
    },
    {
      id: 5,
      name: 'statusText',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = assetStatusList && !assetStatusList.length;

        return (
          <TableCell>
            <FormFieldSelect
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.status`}
              optionsTotal={assetStatusList && assetStatusList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}` : null}
            >
              {menuItemAssetStatusList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 6,
      name: 'streetAddress',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCellExtended isEditMode>
          <FieldAddressBlock isInline>
            <FormFieldAddress
              itemId={assetId}
              name={`${formPrefix}.streetAddress`}
              inputRefs={inputRefs}
              inputRefIndex={`${assetIndex}.${fieldIndex}`}
              onAddressSearchCompleted={(addressObject: JsonObject): void => {
                onAddressSearchCompleted(addressObject, assetId, portfolioId);
              }}
              onKeyDown={(): void => {
                onAddressFieldKeyDown(assetId, portfolioId);
              }}
            />
            <FormFieldText
              isDisabled
              name={`${formPrefix}.location`}
              inputRefs={inputRefs}
            />
            <FormFieldText
              isDisabled
              name={`${formPrefix}.postalCode`}
              inputRefs={inputRefs}
            />
            <FormFieldText isHidden name={`${formPrefix}.address`} />
          </FieldAddressBlock>
        </TableCellExtended>
      ),
    },
    {
      id: 7,
      name: 'addressDetails',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            isInline
            isMultiline
            name={`${formPrefix}.addressDetails`}
            maxRows={FormService.TEXTAREA_MAX_ROWS_SMALL}
            minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 8,
      name: 'msaLocation',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {assetData.msaLocation}
        </TableCell>
      ),
    },
    {
      id: 9,
      name: 'size',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = measureUnitList && !measureUnitList.length;
        const subFieldIndexSize: number = 1;
        const subFieldIndexSizeType: number = 2;

        return (
          <TableCell>
            <FieldSeparatedBlock>
              <FormFieldNumber
                isDisabled={isDisabled}
                name={`${formPrefix}.size`}
                inputRefs={inputRefs}
                inputRefIndex={`${assetIndex}.${fieldIndex}.${subFieldIndexSize}`}
              />
              <FormFieldSelect
                withBlankElement
                isDisabled={isDisabled}
                isReadOnly={isShiftPressed}
                name={`${formPrefix}.sizeType`}
                optionsTotal={measureUnitList && measureUnitList.length}
                inputRefs={inputRefs}
                inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}.${subFieldIndexSizeType}` : null}
              >
                {menuItemMeasureUnitList}
              </FormFieldSelect>
            </FieldSeparatedBlock>
          </TableCell>
        );
      },
    },
    {
      id: 10,
      name: 'amount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.amount`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 11,
      name: 'value',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.value`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 12,
      name: 'loanAmount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.loanAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 13,
      name: 'ltv',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {assetData.ltv}
        </TableCell>
      ),
    },
    {
      id: 14,
      name: 'parcelNumber',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.parcelNumber`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 15,
      name: 'submarket',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.submarket`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 16,
      name: 'lotSize',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.lotSize`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 17,
      name: 'assetOwnershipStructure',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = assetOwnershipStructureTypeList && !assetOwnershipStructureTypeList.length;

        return (
          <TableCell>
            <FormFieldSelect
              withBlankElement
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.assetOwnershipStructure`}
              optionsTotal={assetOwnershipStructureTypeList && assetOwnershipStructureTypeList.length}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}` : null}
            >
              {menuItemAssetOwnershipStructureTypeList}
            </FormFieldSelect>
          </TableCell>
        );
      },
    },
    {
      id: 18,
      name: 'affordableUnits',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.affordableUnits`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 19,
      name: 'asOfRightZoning',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isTextCenter>
          <FormFieldCheckbox
            name={`${formPrefix}.asOfRightZoning`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 20,
      name: 'cost',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.cost`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 21,
      name: 'occupancy',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldText
            name={`${formPrefix}.occupancy`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 22,
      name: 'currentNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.currentNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 23,
      name: 'ttmNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.ttmNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 24,
      name: 'runRateNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.runRateNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 25,
      name: 'forwardNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldNumber
            name={`${formPrefix}.forwardNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 26,
      name: 'maturityDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            isReadOnly={isShiftPressed}
            name={`${formPrefix}.maturityDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 27,
      name: 'lastDeal.capitalizationSummaryData.sources.debt.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {assetData.lastDeal && assetData.lastDeal.capitalizationSummaryData
            && assetData.lastDeal.capitalizationSummaryData.sources
            && assetData.lastDeal.capitalizationSummaryData.sources.debt
            && assetData.lastDeal.capitalizationSummaryData.sources.debt.amount}
        </TableCell>
      ),
    },
    {
      id: 28,
      name: 'lastDeal.capitalizationSummaryData.sources.equity.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {assetData.lastDeal && assetData.lastDeal.capitalizationSummaryData
            && assetData.lastDeal.capitalizationSummaryData.sources
            && assetData.lastDeal.capitalizationSummaryData.sources.equity
            && assetData.lastDeal.capitalizationSummaryData.sources.equity.amount}
        </TableCell>
      ),
    },
    {
      id: 29,
      name: 'lastDeal.capitalizationSummaryData.sources.generalPartner.amount',
      component: (): ReactElement => (
        <TableCell isNumber isDisabled>
          {assetData.lastDeal && assetData.lastDeal.capitalizationSummaryData
            && assetData.lastDeal.capitalizationSummaryData.sources
            && assetData.lastDeal.capitalizationSummaryData.sources.generalPartner
            && assetData.lastDeal.capitalizationSummaryData.sources.generalPartner.amount}
        </TableCell>
      ),
    },
    {
      id: 30,
      name: 'sellers.fullName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = contactCompanyList && !contactCompanyList.length;

        return (
          <TableCell>
            <FormFieldMultiSelect
              withImage
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              name={`${formPrefix}.sellerIds`}
              optionList={contactCompanyList}
              defaultImage={CompanyDefaultImage}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}` : null}
              onSearch={onContactCompanySearch}
              onClose={onContactCompanySearchClose}
              onValuesChange={sellersChange}
            >
              {menuItemContactCompanySellerList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 31,
      name: 'lastDeal.lenders.fullName',
      component: (): ReactElement => (
        <TableCell isText isDisabled>
          {
            assetData.lastDeal && assetData.lastDeal.lenders && !!assetData.lastDeal.lenders.length
            && assetData.lastDeal.lenders.map((contactItem: Contact): ReactElement => (
              <Chip
                isDisabled
                key={contactItem.id}
                label={contactItem.fullName}
                imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
              />
            ))
          }
        </TableCell>
      ),
    },
    {
      id: 32,
      name: 'lastDeal.receivedDate',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.receivedDate}
        </TableCell>
      ),
    },
    {
      id: 33,
      name: 'portfolioInvestedEquity',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.portfolioInvestedEquity`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 34,
      name: 'leveredIrr',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            isPercent
            name={`${formPrefix}.leveredIrr`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 35,
      name: 'loanNumber',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.loanNumber`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 36,
      name: 'equityMultiple',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.equityMultiple`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 37,
      name: 'lastDeal.sponsor',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.sponsor && assetData.lastDeal.sponsor.fullName}
        </TableCell>
      ),
    },
    {
      id: 38,
      name: 'lastDeal.zfaAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.zfaAmount}
        </TableCell>
      ),
    },
    {
      id: 39,
      name: 'lastDeal.gsfAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.gsfAmount}
        </TableCell>
      ),
    },
    {
      id: 40,
      name: 'lastDeal.nsfAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.nsfAmount}
        </TableCell>
      ),
    },
    {
      id: 41,
      name: 'lastDeal.unitsAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.unitsAmount}
        </TableCell>
      ),
    },
    {
      id: 42,
      name: 'lastDeal.keysAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.keysAmount}
        </TableCell>
      ),
    },
    {
      id: 43,
      name: 'lastDeal.acresAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.acresAmount}
        </TableCell>
      ),
    },
    {
      id: 44,
      name: 'lastDeal.lotsfAmount',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.lastDeal && assetData.lastDeal.lotsfAmount}
        </TableCell>
      ),
    },
    {
      id: 45,
      name: 'leedDesignation',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.leedDesignation`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 46,
      name: 'ownerEntityName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.ownerEntityName`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 47,
      name: 'futureNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.futureNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 48,
      name: 'investmentEntityName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.investmentEntityName`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 49,
      name: 'investmentAmountCommitted',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.investmentAmountCommitted`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 50,
      name: 'investmentAmountUnfunded',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.investmentAmountUnfunded`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 51,
      name: 'investmentAmountFunded',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.investmentAmountFunded`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 52,
      name: 'currentMarketValue',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.currentMarketValue`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 53,
      name: 'closeDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.closeDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 54,
      name: 'extensions',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.extensions`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 55,
      name: 'amountInvested',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.amountInvested`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 56,
      name: 'projectedStabilizedNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.projectedStabilizedNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 57,
      name: 'goingInCapRate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.goingInCapRate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 58,
      name: 'untrendedYieldToCost',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.untrendedYieldToCost`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 59,
      name: 'projectedUnleveredIrr',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.projectedUnleveredIrr`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 60,
      name: 'projectedUnleveredEquityMultiple',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.projectedUnleveredEquityMultiple`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 61,
      name: 'projectedUnleveredNetProfit',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.projectedUnleveredNetProfit`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 62,
      name: 'projectedLeveredIrr',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.projectedLeveredIrr`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 63,
      name: 'projectedLeveredEquityMultiple',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.projectedLeveredEquityMultiple`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 64,
      name: 'projectedLeveredNetProfit',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.projectedLeveredNetProfit`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 65,
      name: 'seniorLenderName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.seniorLenderName`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 66,
      name: 'seniorLoanAmount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.seniorLoanAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 67,
      name: 'seniorLoanClosingDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.seniorLoanClosingDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 68,
      name: 'seniorLoanMaturityDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.seniorLoanMaturityDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 69,
      name: 'seniorLoanInterestRate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldSelect
            withBlankElement
            isDisabled={pricingTypeList && !pricingTypeList.length}
            name={`${formPrefix}.seniorLoanInterestRate`}
            optionsTotal={pricingTypeList && pricingTypeList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          >
            {menuItemPricingTypeList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 70,
      name: 'seniorLoanInterestSpread',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.seniorLoanInterestSpread`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 71,
      name: 'seniorLoanLtv',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.seniorLoanLtv`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 72,
      name: 'seniorLoanLtc',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.seniorLoanLtc`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 73,
      name: 'mezzanineLenderName',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.mezzanineLenderName`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 74,
      name: 'mezzanineLoanAmount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.mezzanineLoanAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 75,
      name: 'mezzanineLoanClosingDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.mezzanineLoanClosingDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 76,
      name: 'mezzanineLoanMaturityDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.mezzanineLoanMaturityDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 77,
      name: 'mezzanineLoanInterestRate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldSelect
            withBlankElement
            isDisabled={pricingTypeList && !pricingTypeList.length}
            name={`${formPrefix}.mezzanineLoanInterestRate`}
            optionsTotal={pricingTypeList && pricingTypeList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          >
            {menuItemPricingTypeList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 78,
      name: 'mezzanineLoanInterestSpread',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.mezzanineLoanInterestSpread`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 79,
      name: 'mezzanineLoanLtv',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.mezzanineLoanLtv`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 80,
      name: 'mezzanineLoanLtc',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.mezzanineLoanLtc`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 81,
      name: 'blendedLoanInterestRate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldSelect
            withBlankElement
            isDisabled={pricingTypeList && !pricingTypeList.length}
            name={`${formPrefix}.blendedLoanInterestRate`}
            optionsTotal={pricingTypeList && pricingTypeList.length}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          >
            {menuItemPricingTypeList}
          </FormFieldSelect>
        </TableCell>
      ),
    },
    {
      id: 82,
      name: 'blendedLoanInterestSpread',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.blendedLoanInterestSpread`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 83,
      name: 'yardiAssetId',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.yardiAssetId`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 84,
      name: 'yardiAppraisedValue',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiAppraisedValue}
        </TableCell>
      ),
    },
    {
      id: 85,
      name: 'yardiLoanBalance',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLoanBalance}
        </TableCell>
      ),
    },
    {
      id: 86,
      name: 'yardiSeniorLoan',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiSeniorLoan}
        </TableCell>
      ),
    },
    {
      id: 87,
      name: 'yardiMarketValueLoan',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiMarketValueLoan}
        </TableCell>
      ),
    },
    {
      id: 88,
      name: 'yardiLoanClose',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLoanClose}
        </TableCell>
      ),
    },
    {
      id: 89,
      name: 'yardiLoanMaturity',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLoanMaturity}
        </TableCell>
      ),
    },
    {
      id: 90,
      name: 'yardiLoanCommitment',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLoanCommitment}
        </TableCell>
      ),
    },
    {
      id: 91,
      name: 'yardiLoanContribution',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLoanContribution}
        </TableCell>
      ),
    },
    {
      id: 92,
      name: 'yardiUnfundedLoan',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiUnfundedLoan}
        </TableCell>
      ),
    },
    {
      id: 93,
      name: 'yardiManagementFeePercent',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiManagementFeePercent}
        </TableCell>
      ),
    },
    {
      id: 94,
      name: 'yardiCurrentManagementFee',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiCurrentManagementFee}
        </TableCell>
      ),
    },
    {
      id: 95,
      name: 'yardiCurrentInterest',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiCurrentInterest}
        </TableCell>
      ),
    },
    {
      id: 96,
      name: 'yardiItdInterest',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiItdInterest}
        </TableCell>
      ),
    },
    {
      id: 97,
      name: 'yardiCurrentAmortization',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiCurrentAmortization}
        </TableCell>
      ),
    },
    {
      id: 98,
      name: 'yardiItdAmortization',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiItdAmortization}
        </TableCell>
      ),
    },
    {
      id: 99,
      name: 'yardiIrrOnLoan',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiIrrOnLoan}
        </TableCell>
      ),
    },
    {
      id: 100,
      name: 'yardiEquityMultiple',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiEquityMultiple}
        </TableCell>
      ),
    },
    {
      id: 101,
      name: 'yardiGainOnLoan',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiGainOnLoan}
        </TableCell>
      ),
    },
    {
      id: 102,
      name: 'yardiLtv',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLtv}
        </TableCell>
      ),
    },
    {
      id: 103,
      name: 'yardiCurrentSofr',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiCurrentSofr}
        </TableCell>
      ),
    },
    {
      id: 104,
      name: 'yardiLoanSpread',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiLoanSpread}
        </TableCell>
      ),
    },
    {
      id: 105,
      name: 'yardiCurrentRate',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiCurrentRate}
        </TableCell>
      ),
    },
    {
      id: 106,
      name: 'yardiFirstExtensionTerm',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiFirstExtensionTerm}
        </TableCell>
      ),
    },
    {
      id: 107,
      name: 'yardiSecondExtensionTerm',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiSecondExtensionTerm}
        </TableCell>
      ),
    },
    {
      id: 108,
      name: 'yardiBorrower',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiBorrower}
        </TableCell>
      ),
    },
    {
      id: 109,
      name: 'yardiSponsor',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiSponsor}
        </TableCell>
      ),
    },
    {
      id: 110,
      name: 'yardiSponsorAdditional',
      component: (): ReactElement => (
        <TableCell isDisabled>
          {assetData.yardiSponsorAdditional}
        </TableCell>
      ),
    },
    {
      id: 111,
      name: 'notePrincipalBalanceAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.notePrincipalBalanceAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 112,
      name: 'accruedInterest',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.accruedInterest`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 113,
      name: 'marketType',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.marketType`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 114,
      name: 'yearBuilt',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.yearBuilt`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 115,
      name: 'yearRenovated',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.yearRenovated`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 116,
      name: 'firstPaymentDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.firstPaymentDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 117,
      name: 'nextPaymentDueDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.nextPaymentDueDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 118,
      name: 'originalLoanTermMonth',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.originalLoanTermMonth`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 119,
      name: 'loanAge',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.loanAge`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 120,
      name: 'remainingTerm',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.remainingTerm`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 121,
      name: 'amortizationPeriodMonths',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.amortizationPeriodMonths`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 122,
      name: 'remainingAmortizationPeriod',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.remainingAmortizationPeriod`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 123,
      name: 'paymentStringYear',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.paymentStringYear`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 124,
      name: 'amortizationType',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.amortizationType`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 125,
      name: 'interestOnlyPeriod',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.interestOnlyPeriod`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 126,
      name: 'remainingInterestOnlyPeriod',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.remainingInterestOnlyPeriod`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 127,
      name: 'interestAccrualMethod',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.interestAccrualMethod`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 128,
      name: 'primaryLoanPurpose',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.primaryLoanPurpose`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 129,
      name: 'underlyingLoanType',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.underlyingLoanType`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 130,
      name: 'lienPosition',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            withNoCommas
            name={`${formPrefix}.lienPosition`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 131,
      name: 'primaryCollateral',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.primaryCollateral`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 132,
      name: 'securityInterest',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.securityInterest`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 133,
      name: 'groundLease',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldCheckbox
            name={`${formPrefix}.groundLease`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 134,
      name: 'prepaymentProvisions',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.prepaymentProvisions`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 135,
      name: 'remainingPrepaymentProvisions',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.remainingPrepaymentProvisions`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 136,
      name: 'currentCreditLetterAmount',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.currentCreditLetterAmount`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 137,
      name: 'currentCreditLetterCreditExpirationDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.currentCreditLetterCreditExpirationDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 138,
      name: 'interestReservesAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.interestReservesAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 139,
      name: 'taxInsuranceEscrowsAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.taxInsuranceEscrowsAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 140,
      name: 'capitalExpenditureReservesAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.capitalExpenditureReservesAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 141,
      name: 'otherEscrowAndReservesAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.otherEscrowAndReservesAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 142,
      name: 'currentInterestReserves',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.currentInterestReserves`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 143,
      name: 'currentTaxInsuranceEscrows',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.currentTaxInsuranceEscrows`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 144,
      name: 'currentCapitalExpenditureEscrows',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.currentCapitalExpenditureEscrows`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 145,
      name: 'currentOtherEscrowsAndReserves',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.currentOtherEscrowsAndReserves`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 146,
      name: 'initialAppraisalDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.initialAppraisalDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 147,
      name: 'stabilizedAppraisedNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.stabilizedAppraisedNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 148,
      name: 'stabilizedOccupancy',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.stabilizedOccupancy`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 149,
      name: 'stabilizedCapitalizationRate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.stabilizedCapitalizationRate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 150,
      name: 'mostRecentAppraisalDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.mostRecentAppraisalDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 151,
      name: 'atCloseRevenue',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.atCloseRevenue`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 152,
      name: 'atCloseExpenses',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.atCloseExpenses`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 153,
      name: 'atCloseNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            name={`${formPrefix}.atCloseNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 154,
      name: 'atCloseDebtYieldNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.atCloseDebtYieldNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 155,
      name: 'atCloseDscrNoi',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.atCloseDscrNoi`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 156,
      name: 'atCloseNcf',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.atCloseNcf`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 157,
      name: 'atCloseDebtYieldNcf',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.atCloseDebtYieldNcf`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 158,
      name: 'atCloseDscrNcf',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.atCloseDscrNcf`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 159,
      name: 'occupancyAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isNumber>
          <FormFieldNumber
            isFloat
            name={`${formPrefix}.occupancyAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 160,
      name: 'atCloseNoiAndOccupancyDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.atCloseNoiAndOccupancyDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 161,
      name: 'stabilizedNoiAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.stabilizedNoiAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 162,
      name: 'stabilizedNcfAtOrigination',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldText
            name={`${formPrefix}.stabilizedNcfAtOrigination`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 163,
      name: 'firstExtendedMaturityDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.firstExtendedMaturityDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 164,
      name: 'secondExtendedMaturityDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.secondExtendedMaturityDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 165,
      name: 'thirdExtendedMaturityDate',
      component: (tableField: TableField, fieldIndex: number): ReactElement => (
        <TableCell isText>
          <FormFieldDate
            withBlankValues
            name={`${formPrefix}.thirdExtendedMaturityDate`}
            inputRefs={inputRefs}
            inputRefIndex={`${assetIndex}.${fieldIndex}`}
          />
        </TableCell>
      ),
    },
    {
      id: 166,
      name: 'types',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = assetTypeList && !assetTypeList.length;

        return (
          <TableCell isText>
            <FormFieldMultiSelect
              withSearch
              isDisabled={isDisabled}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.types`}
              optionList={assetTypeList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}` : null}
              onValuesChange={assetTypesChange}
            >
              {menuItemAssetTypeList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 167,
      name: 'tags',
      component: (tableField: TableField, fieldIndex: number): ReactElement => {
        const isDisabled: boolean = tagList && !tagList.length;

        return (
          <TableCell isText>
            <FormFieldMultiSelect
              isAbleToAdd
              withSearch
              isDisabled={tagList && !tagList.length}
              isReadOnly={isShiftPressed}
              keyField="name"
              name={`${formPrefix}.tags`}
              optionList={tagList}
              inputRefs={inputRefs}
              inputRefIndex={!isDisabled ? `${assetIndex}.${fieldIndex}` : null}
              onSearch={onTagSearch}
              onClose={onTagSearchClose}
              onValuesChange={tagsChange}
            >
              {menuItemTagList}
            </FormFieldMultiSelect>
          </TableCell>
        );
      },
    },
    {
      id: 168,
      name: 'actions',
      isNotFiltered: true,
      component: (): ReactElement => (
        <TableCell isActions isTextRight isSticky isStickyRight>
          {
            isSaving && isInlineSaving && (
              <Loader isInline isSmall />
            )
          }
          {
            !isSaveDisabled && !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineSave(portfolioData.id, assetData.id);
                }}
              >
                <IconBlockSuccess />
              </ActionButton>
            )
          }
          {
            !isSaving && (
              <ActionButton
                withSmallPadding
                onClick={(): void => {
                  onInlineCancel(portfolioData.id, assetData.id);
                }}
              >
                <IconBlockError isDisabled={isSaveDisabled} />
              </ActionButton>
            )
          }
        </TableCell>
      ),
    },
  ], [
    isShiftPressed, assetPhotoFile, selectedTags, selectedAssetTypes, selectedAssetSellers, fileDropDeps,
    isSaveDisabled, isSaving, assetData, contactCompanyList, tagList,
  ]);

  useEffect((): void => {
    if (assetData) {
      if (!selectedAssetSellers) {
        setSelectedAssetSellers(assetData.sellers || []);
      }

      if (!selectedAssetTypes) {
        setSelectedAssetTypes(assetData.types || []);
      }

      if (!selectedTags) {
        setSelectedTags(assetData.tags || []);
      }
    }
  }, [assetData]);

  return (
    <TableRowForm isActive={isActive}>
      {
        tableAssetListFields && !!tableAssetListFields.length
        && StructureService.extendOptions(tableAssetListFields, tableColumnSettings, true)
          .map((tableField: TableField, fieldIndex: number): ReactElement | boolean => (
            !!tableField.component && (
              !tableField.isNotFiltered ? (
                <FilteredCell
                  key={tableField.id}
                  tableName={TABLE_NAME_ASSET_LIST}
                  columnName={tableField.name}
                  currentUser={currentUser}
                >
                  {tableField.component(tableField, fieldIndex)}
                </FilteredCell>
              ) : (
                <Fragment key={tableField.id}>
                  {tableField.component(tableField, fieldIndex)}
                </Fragment>
              )
            )
          ))
      }
    </TableRowForm>
  );
});

export default AssetRowForm;
